import { CONSTANT } from "./Constant";
import { notification } from "antd";
import { debounce } from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// UTC plugin lets us force-interpret a server timestamp as UTC before shifting to local.
dayjs.extend(utc);

export const dateFormatter = (
  date,
  format = CONSTANT.DATE_FORMATS.DMY_slash,
) => {
  return dayjs(date).format(format);
};

/**
 * Convert a UTC datetime from the API into the viewer's (browser's) local timezone and format it.
 *
 * The backend stores/serves timestamps in UTC, but they are often serialized WITHOUT a trailing "Z"
 * (e.g. "2026-09-01T12:57:29.418"). The native Date parser treats a timezone-less string as LOCAL,
 * so no conversion happens and the raw UTC value is shown as-is. `dayjs.utc(value)` forces UTC
 * interpretation; `.local()` then shifts to the browser timezone. Values that already carry a "Z"
 * or an explicit offset are handled correctly too.
 *
 * Use this for ANY UTC timestamp coming from the API that should be displayed to the user.
 *
 * @param {string|Date|dayjs.Dayjs} value  UTC datetime from the API
 * @param {string} format  dayjs format (default: DD-MM-YYYY hh:mm:ss A)
 * @param {string} fallback  returned when value is empty/invalid (default: "")
 */
export const formatUtcToLocal = (
  value,
  format = CONSTANT.DATE_FORMATS.DMYHMSA,
  fallback = "",
) => {
  if (!value) return fallback;
  const d = dayjs.utc(value).local();
  return d.isValid() ? d.format(format) : fallback;
};

export const getLocalStorageData = (key) => {
  return JSON.parse(
    localStorage.getItem(key) ? localStorage.getItem(key) : null,
  );
};

export const setLocalStorageData = (key, data) => {
  localStorage.setItem(key, JSON.stringify(data));
};

export const generateUniqueNumber = () => {
  return Date.now() + Math.floor(Math.random() * 1000);
};

export const ensureDropdownIncludesValue = (list, value) => {
  if (!value) return list;
  const exists = list.some((item) => item.id === value.id);
  return exists ? list : [...list, value];
};

export const validateFields = debounce((form, setDisabled) => {
  form
    .validateFields()
    .then(() => {
      setDisabled(false);
    })
    .catch(() => {
      setDisabled(true);
    });
}, 500);

export const dataToFormdataConverter = (data) => {
  const formData = new FormData();
  for (let name in data) {
    const value = data[name];

    if (value !== "" && value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        name = `${name}[]`;
        value.forEach((item_values) => {
          formData.append(name, item_values);
        });
      } else {
        formData.append(name, value);
      }
    }
    // formData.append(name, data[name]); // there should be values.avatar which is a File object
  }

  return formData;
};

const getNotificationPlacement = (placement) => {
  if (placement) return placement;

  // Simple mobile check
  const isMobile = window.innerWidth <= 768;

  return isMobile ? "topRight" : "bottomLeft";
};

export const Notification = {
  success: (data) => {
    notification.success({
      placement: getNotificationPlacement(data.placement),
      duration: 5,
      key: Math.random().toString(),
      ...data,
    });
  },
  error: (data) => {
    notification.error({
      placement: getNotificationPlacement(data.placement),
      duration: 5,
      key: Math.random().toString(),
      ...data,
    });
  },
  warning: (data) => {
    notification.warning({
      placement: getNotificationPlacement(data.placement),
      duration: 5,
      key: Math.random().toString(),
      ...data,
    });
  },
  info: (data) => {
    notification.info({
      placement: getNotificationPlacement(data.placement),
      duration: 5,
      key: Math.random().toString(),
      ...data,
    });
  },
};

export const appendFormData = (formData, data, parentKey = "") => {
  if (data instanceof Blob || data instanceof File) {
    // If it's a Blob or File, append it directly
    formData.append(parentKey, data);
  } else if (
    data &&
    typeof data === "object" &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      appendFormData(
        formData,
        data[key],
        parentKey ? `${parentKey}[${key}]` : key,
      );
    });
  } else {
    formData.append(parentKey, data);
  }
};

export const convertToFormData = (nestedObject) => {
  const formData = new FormData();
  appendFormData(formData, nestedObject);
  return formData;
};

export const replacePlaceholders = (content, placeholders) => {
  let replacedContent = content;

  placeholders.forEach((placeholder) => {
    // Simple split and join approach
    replacedContent = replacedContent
      .split(placeholder.name)
      .join(placeholder.value);
  });

  return replacedContent;
};

export const formatDateToISO = (dateValue) => {
  if (!dateValue) return null;
  // Ensure we have a valid dayjs object
  const dayjsDate = dayjs.isDayjs(dateValue) ? dateValue : dayjs(dateValue);
  if (!dayjsDate.isValid()) return null;
  // Format to ISO UTC string
  return dayjsDate.format("YYYY-MM-DDTHH:mm:ss") + "Z";
};
